(() => {
  if (window.__stockWarningPopupInit) return;
  window.__stockWarningPopupInit = true;

  const settings = window.stockWarningPopupData ?? {};
  const t = settings.strings ?? {};

  const fill = (template, values) =>
    Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`[${key}]`, value),
      template
    );

  const describe = (added) =>
    fill(
      t.item ??
        "Only <strong>[added]</strong> item(s) were added to your cart due to availability. For orders exceeding current stock, contact customer service.",
      { added }
    );

  const openChat = () => {
    const chat = window.GorgiasChat;
    if (!chat) return;

    const open = () => chat.open?.();
    if (typeof chat.init === "function") {
      Promise.resolve(chat.init()).then(open, open);
    } else {
      open();
    }
  };

  const showNotice = (added) => {
    const lastFocus = document.activeElement;
    const hasChat = Boolean(window.GorgiasChat);

    const overlay = document.createElement("div");
    overlay.className = "stock-warning-popup";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "stock-warning-popup-title");
    overlay.innerHTML = `
      <div class="stock-warning-popup__dialog" role="document">
        <h2 class="stock-warning-popup__title" id="stock-warning-popup-title">${t.title ?? "Limited stock available"}</h2>
        ${t.intro ? `<p class="stock-warning-popup__intro">${t.intro}</p>` : ""}
        <ul class="stock-warning-popup__list">
          <li class="stock-warning-popup__item">${describe(added)}</li>
        </ul>
        <div class="stock-warning-popup__actions">
          <button type="button" class="btn btn--secondary stock-warning-popup__btn stock-warning-popup__btn--cancel">${t.close ?? "Close"}</button>
          ${hasChat ? `<button type="button" class="btn stock-warning-popup__btn stock-warning-popup__btn--confirm">${t.chat ?? "Chat with us"}</button>` : ""}
        </div>
      </div>`;

    let done = false;
    const close = (restoreFocus = true) => {
      if (done) return;
      done = true;
      overlay.classList.remove("is-open");
      document.removeEventListener("keydown", onKey, true);
      setTimeout(() => {
        overlay.remove();
        if (restoreFocus) lastFocus?.focus?.();
      }, 180);
    };

    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      }
    };

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) close();
    });
    overlay.querySelector(".stock-warning-popup__btn--cancel").onclick = () =>
      close();

    const chatBtn = overlay.querySelector(".stock-warning-popup__btn--confirm");
    if (chatBtn) {
      chatBtn.onclick = () => {
        close(false);
        openChat();
      };
    }

    document.addEventListener("keydown", onKey, true);

    document.body.append(overlay);
    void overlay.offsetWidth;
    overlay.classList.add("is-open");
    (chatBtn ?? overlay.querySelector(".stock-warning-popup__btn--cancel")).focus();
  };

  const notify = (added) => {
    try {
      showNotice(added);
    } catch (error) {
      console.debug("[stock-warning-popup] dialog failed", error);
    }
  };

  const readStockData = (form) => {
    const el = form?.querySelector("script[data-stock-warning]");
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  };

  const mainFormFor = (form) =>
    form
      .closest(".section-main-product")
      ?.querySelector("buy-buttons form.js-product-form") ?? null;

  const productFormEntry = (form) => {
    const sourceForm = readStockData(form) ? form : mainFormFor(form);
    const data = readStockData(sourceForm);
    if (!data?.variants) return null;

    const id = form.querySelector('[name="id"]')?.value;
    const variant = id ? data.variants[id] : null;
    if (!variant) return null;

    const quantityInput =
      form.querySelector('[name="quantity"]:not([data-copied])') ??
      sourceForm.querySelector('[name="quantity"]');
    const quantity = parseInt(quantityInput?.value, 10) || 1;
    if (quantity <= variant.available) return null;

    return {
      variantId: Number(id),
      available: variant.available,
      quantity,
      quantityInput,
    };
  };

  const cartQuantityOf = async (variantId) => {
    try {
      const res = await fetch(`${window.theme?.routes?.cart ?? "/cart"}.js`, {
        credentials: "same-origin",
      });
      const cart = res.ok ? await res.json() : null;
      return (cart?.items ?? [])
        .filter((item) => item.variant_id === variantId)
        .reduce((sum, item) => sum + item.quantity, 0);
    } catch (error) {
      console.debug("[stock-warning-popup] cart read failed", error);
      return 0;
    }
  };

  const suppressCartDrawerOpen = () => {
    const drawer = document.querySelector(".js-cart-drawer");
    if (!drawer || Object.prototype.hasOwnProperty.call(drawer, "open")) return;

    let timer;
    const restore = () => {
      clearTimeout(timer);
      delete drawer.open;
      document.removeEventListener("on:cart:add", restore);
      document.removeEventListener("on:cart:error", restore);
    };

    drawer.open = () => {};
    document.addEventListener("on:cart:add", restore);
    document.addEventListener("on:cart:error", restore);
    timer = setTimeout(restore, 15000);
  };

  const addAvailableUnits = async (form, entry) => {
    const inCart = await cartQuantityOf(entry.variantId);
    const added = Math.max(Math.min(entry.quantity, entry.available - inCart), 0);

    notify(added);
    if (!added || !entry.quantityInput) return;

    const { quantityInput } = entry;
    const requested = quantityInput.value;
    quantityInput.value = added;
    suppressCartDrawerOpen();
    if (form.requestSubmit) form.requestSubmit();
    else form.submit();
    quantityInput.value = requested;
  };

  window.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!form?.matches?.("form.js-product-form")) return;

      const entry = productFormEntry(form);
      if (!entry) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      addAvailableUnits(form, entry);
    },
    true
  );

  const cartLimit = (input) => {
    const available = parseInt(input?.dataset.stockAvailable, 10);
    return available > 0 ? available : null;
  };

  window.addEventListener(
    "click",
    (event) => {
      const button = event.target?.closest?.("cart-form .quantity-up");
      if (!button) return;

      const input = button
        .closest(".cart-item")
        ?.querySelector(".cart-item__quantity-input");
      const available = cartLimit(input);
      if (!available) return;

      const current = parseInt(input.value, 10) || 0;
      if (current + 1 <= available) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      notify(available);
    },
    true
  );

  window.addEventListener(
    "change",
    (event) => {
      const input = event.target;
      if (!input?.matches?.("cart-form .cart-item__quantity-input")) return;

      const available = cartLimit(input);
      if (!available) return;

      const quantity = parseInt(input.value, 10) || 0;
      const previous = parseInt(input.dataset.initialValue, 10) || 0;
      if (quantity <= previous || quantity <= available) return;

      const capped = Math.max(available, previous);
      input.value = capped;
      if (capped === previous) event.stopImmediatePropagation();

      notify(capped);
    },
    true
  );
})();
